import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface MetaSummaryHopaDailyDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** Data loader for HOPA/META by date charts.
 */
export class MetaSummaryHopaDailyDataLoaderFactory extends Factory<
    DataLoader,
    MetaSummaryHopaDailyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'MetaSummaryHopaDailyDataLoaderFactory';

    getInstance(options: MetaSummaryHopaDailyDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { dateTrunc, splitSelect, splitGroupBy, where, join } =
                    BiVizDashboardUsersActivity.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT
  ${splitGroupBy}date,
  -- "per active user": all active users count, no deco data = 0
  -- players that do not place any decos deliberately drag the stats down.
  APPROX_QUANTILES(COALESCE(new_decos_placed, 0), 100)[OFFSET(50)] AS median_decos_placed,
  AVG(COALESCE(new_decos_placed, 0)) AS avg_decos_placed,
FROM (
  -- date aggregation keeps THE PLAYER as the unit: SUM each player's activity over the
  -- period first, then aggregate across players — never over pooled player-days.
  SELECT
    ${splitSelect}${dateTrunc} AS date,
    player_id,
    SUM(new_decos_placed) AS new_decos_placed
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      datasourceSqlMap.dataset,
      datasourceSqlMap.tableId
  )}\` ds
  ${join}
  ${where}
  GROUP BY ${splitGroupBy}date, player_id
)
GROUP BY ${splitGroupBy}date
ORDER BY ${splitGroupBy}date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
